// Simple technical-indicator helpers operating on arrays of closing prices.
// Each function returns an array the same length as `values`, with `null`
// for indices where there isn't enough history to compute a value yet.

export function sma(values, period) {
  const result = new Array(values.length).fill(null);
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i];
    if (i >= period) sum -= values[i - period];
    if (i >= period - 1) result[i] = sum / period;
  }
  return result;
}

export function ema(values, period) {
  const result = new Array(values.length).fill(null);
  const k = 2 / (period + 1);
  let prev = null;
  for (let i = 0; i < values.length; i++) {
    if (i < period - 1) continue;
    if (prev === null) {
      let sum = 0;
      for (let j = i - period + 1; j <= i; j++) sum += values[j];
      prev = sum / period;
    } else {
      prev = values[i] * k + prev * (1 - k);
    }
    result[i] = prev;
  }
  return result;
}

export function rsi(values, period = 14) {
  const result = new Array(values.length).fill(null);
  if (values.length <= period) return result;

  let gainSum = 0;
  let lossSum = 0;
  for (let i = 1; i <= period; i++) {
    const change = values[i] - values[i - 1];
    if (change > 0) gainSum += change;
    else lossSum -= change;
  }
  let avgGain = gainSum / period;
  let avgLoss = lossSum / period;
  result[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);

  for (let i = period + 1; i < values.length; i++) {
    const change = values[i] - values[i - 1];
    const gain = change > 0 ? change : 0;
    const loss = change < 0 ? -change : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    result[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return result;
}

export function macd(values, fast = 12, slow = 26, signalPeriod = 9) {
  const fastEma = ema(values, fast);
  const slowEma = ema(values, slow);
  const macdLine = values.map((_, i) => {
    const f = fastEma[i];
    const s = slowEma[i];
    return f !== null && s !== null ? f - s : null;
  });

  const defined = macdLine.filter((v) => v !== null);
  const signalDefined = ema(defined, signalPeriod);
  const signalLine = new Array(values.length).fill(null);
  let di = 0;
  for (let i = 0; i < macdLine.length; i++) {
    if (macdLine[i] === null) continue;
    signalLine[i] = signalDefined[di];
    di++;
  }

  const histogram = values.map((_, i) => {
    const m = macdLine[i];
    const sg = signalLine[i];
    return m !== null && sg !== null ? m - sg : null;
  });

  return { macd: macdLine, signal: signalLine, histogram };
}

export function bollinger(values, period = 20, stddev = 2) {
  const upper  = new Array(values.length).fill(null);
  const middle = new Array(values.length).fill(null);
  const lower  = new Array(values.length).fill(null);
  for (let i = period - 1; i < values.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += values[j];
    const mean = sum / period;
    let sq = 0;
    for (let j = i - period + 1; j <= i; j++) { const d = values[j] - mean; sq += d * d; }
    const sd = Math.sqrt(sq / period);
    upper[i]  = mean + stddev * sd;
    middle[i] = mean;
    lower[i]  = mean - stddev * sd;
  }
  return { upper, middle, lower };
}
